import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, ChevronRight, LogOut, Clock } from "lucide-react";
import { toast } from "sonner";
import { QUESTIONS, SCALE_LABELS, DEFAULT_ANSWERS } from "../lib/questionnaire";

// Per-user localStorage key — keeps drafts isolated when several accounts
// share the same browser.
const draftKey = (uid) => `soulmate_onboarding_draft_${uid || "anon"}`;

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(DEFAULT_ANSWERS);
  const [submitting, setSubmitting] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // Defense in depth: an admin should NEVER see the questionnaire.
  useEffect(() => {
    if (user?.is_admin) navigate("/admin", { replace: true });
    else if (user?.onboarding_complete) navigate("/discover", { replace: true });
  }, [user, navigate]);

  // Restore draft on mount (per-user)
  useEffect(() => {
    if (!user?.id) return;
    try {
      const raw = localStorage.getItem(draftKey(user.id));
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft && typeof draft === "object") {
        if (draft.answers) setAnswers({ ...DEFAULT_ANSWERS, ...draft.answers });
        if (typeof draft.step === "number") setStep(Math.max(0, Math.min(draft.step, QUESTIONS.length - 1)));
        const filled = draft.answers ? Object.keys(draft.answers).length : 0;
        if (filled > 3) {
          setDraftRestored(true);
          toast.success(`Bem-vindo de volta. Retomámos o teste onde paraste (${filled} respostas guardadas).`, { duration: 5000 });
          // Hide the "restored" badge after 8s
          setTimeout(() => setDraftRestored(false), 8000);
        }
      }
    } catch { /* corrupted draft — ignore */ }
    // Only restore on first mount per user — user.id is stable for the
    // lifetime of this component (we redirect on logout), so no further
    // deps are needed.
  }, [user?.id]);

  // Auto-save the draft on every step/answer change (debounced via setTimeout)
  useEffect(() => {
    if (!user?.id) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey(user.id), JSON.stringify({
          step, answers, savedAt: new Date().toISOString(),
        }));
      } catch { /* quota — ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [step, answers, user?.id]);

  const clearDraft = () => {
    try { if (user?.id) localStorage.removeItem(draftKey(user.id)); } catch { /* noop */ }
  };

  const continueLater = () => {
    const filled = Object.keys(answers).filter(k => {
      const v = answers[k];
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== "" && v !== DEFAULT_ANSWERS[k];
    }).length;
    const ok = window.confirm(
      filled > 0
        ? `Continuar mais tarde?\n\nGuardámos o teu progresso (${filled} respostas). Terás de fazer login com o mesmo email para retomar exactamente onde paraste.`
        : "Sair do questionário e terminar sessão?"
    );
    if (!ok) return;
    // Draft kept in localStorage on purpose — that's the whole point of "later".
    logout();
    toast.success(filled > 0 ? "Progresso guardado. Volta quando quiseres." : "Sessão terminada.");
    navigate("/login", { replace: true });
  };

  const discardAndExit = () => {
    const ok = window.confirm(
      "Apagar o teste e sair?\n\nTodas as respostas serão eliminadas. Esta acção não pode ser desfeita."
    );
    if (!ok) return;
    clearDraft();
    logout();
    toast.success("Teste apagado. Sessão terminada.");
    navigate("/login", { replace: true });
  };

  const total = QUESTIONS.length;
  const q = QUESTIONS[step];

  const sections = useMemo(() => {
    const s = new Map();
    QUESTIONS.forEach((qq, i) => {
      if (!s.has(qq.section)) s.set(qq.section, { from: i, to: i });
      else s.get(qq.section).to = i;
    });
    return Array.from(s.entries());
  }, []);

  const sectionIdx = sections.findIndex(([, r]) => step >= r.from && step <= r.to);
  const totalSections = sections.length;

  const value = q.type === "range_pair" ? null : answers[q.key];

  const set = (v) => setAnswers((s) => ({ ...s, [q.key]: v }));
  const setKey = (k, v) => setAnswers((s) => ({ ...s, [k]: v }));

  const canNext = () => {
    if (q.type === "scale") return value !== undefined;
    if (q.type === "choice") return !!value;
    if (q.type === "multi") return Array.isArray(value) && value.length >= (q.min || 1);
    if (q.type === "keywords") return Array.isArray(value) && value.length >= (q.min || 1);
    if (q.type === "number") return value !== undefined;
    if (q.type === "range_pair") {
      const lo = answers[q.minKey], hi = answers[q.maxKey];
      return lo !== undefined && hi !== undefined && lo <= hi;
    }
    if (q.type === "text") return (value || "").trim().length >= 3;
    return false;
  };

  const next = async () => {
    if (step < total - 1) { setStep(step + 1); return; }
    setSubmitting(true);
    try {
      // Coerce numeric fields
      const payload = { ...answers };
      const intKeys = [
        "o1","o2","o3","c1","c2","c3","e1","e2","e3","a1","a2","a3","n1","n2","n3",
        "religion_importance","appearance_importance","education_importance",
        "ambition_importance","intellect_importance","going_out_frequency",
        "family_closeness_importance","friends_importance","shared_practice_importance",
        "mindfulness_importance","ambition_level","pref_height_min","pref_height_max",
        "preferred_age_min","preferred_age_max","preferred_distance_km",
      ];
      intKeys.forEach(k => { if (payload[k] !== undefined) payload[k] = Number(payload[k]); });

      await client.post("/questionnaire", payload);
      await refresh();
      // Successfully submitted — draft no longer needed.
      clearDraft();
      toast.success("Questionário concluído. A calibrar afinidades...");
      // Flag for the next page to show the welcome-tokens modal once.
      try { localStorage.setItem("soulmate_show_welcome_tokens", "1"); } catch { /* noop */ }
      navigate("/discover");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao guardar respostas.");
    } finally { setSubmitting(false); }
  };

  const back = () => step > 0 && setStep(step - 1);
  const progress = Math.round(((step + 1) / total) * 100);

  return (
    <div className="min-h-screen bg-[#0C0E12] text-[#F5F2EE] flex flex-col" data-testid="onboarding-page">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between gap-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <span className="font-display text-xl">Soulmatch</span>
          {draftRestored && (
            <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 text-[10px] uppercase tracking-wider text-[#C5A880]"
              data-testid="draft-restored-badge">
              Progresso restaurado
            </span>
          )}
        </div>
        <div className="text-center hidden sm:block">
          <span className="label-uppercase block">{q.section}</span>
          <span className="text-[10px] text-[#A29F9B] tracking-wider">Secção {sectionIdx + 1} de {totalSections}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#A29F9B] tabular-nums" data-testid="onboarding-progress">{step + 1} / {total}</span>
          <button onClick={continueLater} type="button"
            className="inline-flex items-center gap-1.5 text-xs text-[#DCD7D2] hover:text-[#C5A880] transition-colors px-3 py-1.5 rounded-full border border-white/10 hover:border-[#C5A880]/40"
            title="Sai mantendo o progresso — retomas onde paraste"
            data-testid="onboarding-continue-later">
            <Clock className="w-3 h-3" /> <span className="hidden sm:inline">Continuar mais tarde</span><span className="sm:hidden">Pausa</span>
          </button>
          <button onClick={discardAndExit} type="button"
            className="inline-flex items-center gap-1.5 text-xs text-[#A29F9B] hover:text-[#E37B7B] transition-colors px-2 py-1.5"
            title="Apagar respostas e sair"
            data-testid="onboarding-discard">
            <LogOut className="w-3 h-3" />
          </button>
        </div>
      </header>

      <div className="h-1 w-full bg-white/5">
        <div className="h-full bg-[#C5A880] transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      <main className="flex-1 flex items-center justify-center px-6 md:px-12 py-10">
        <div className="w-full max-w-2xl animate-fade-in-up" key={step}>
          <h1 className="font-display text-3xl md:text-5xl leading-tight mb-3" data-testid="onboarding-question">
            {q.title}
          </h1>
          {q.subtitle && <p className="text-[#A29F9B] mb-8">{q.subtitle}</p>}
          <div className="mt-6">

          {q.type === "scale" && (
            <div data-testid="onboarding-scale">
              <div className="flex justify-between text-xs text-[#A29F9B] mb-4">
                <span>{q.labels?.[0] || SCALE_LABELS[0]}</span>
                <span>{q.labels?.[1] || ""}</span>
                <span>{q.labels?.[2] || SCALE_LABELS[2]}</span>
              </div>
              <div className="grid grid-cols-5 gap-3">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} onClick={()=>set(n)}
                    data-testid={`scale-${q.key}-${n}`}
                    className={`py-6 rounded-2xl border transition-all ${
                      value === n
                        ? "bg-[#C5A880] text-[#0C0E12] border-[#C5A880]"
                        : "bg-[#161920] border-white/5 hover:border-[#C5A880]/40 text-[#DCD7D2]"
                    }`}>
                    <span className="text-xl font-display">{n}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {q.type === "choice" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="onboarding-choice">
              {q.options.map((opt) => (
                <button key={opt.v} onClick={()=>set(opt.v)}
                  data-testid={`choice-${q.key}-${opt.v}`}
                  className={`text-left p-5 rounded-2xl border transition-all ${
                    value === opt.v
                      ? "bg-[#C5A880]/10 border-[#C5A880] text-[#F5F2EE]"
                      : "bg-[#161920] border-white/5 hover:border-[#C5A880]/40"
                  }`}>
                  <span className="font-display text-lg">{opt.l}</span>
                </button>
              ))}
            </div>
          )}

          {q.type === "multi" && (
            <div data-testid="onboarding-multi">
              <div className="flex flex-wrap gap-3">
                {q.options.map((opt) => {
                  const selected = (value || []).includes(opt);
                  const limitReached = !selected && (value || []).length >= (q.max || 99);
                  return (
                    <button key={opt}
                      data-testid={`multi-${q.key}-${opt}`}
                      onClick={()=>{
                        if (selected) set((value || []).filter(x => x !== opt));
                        else if (!limitReached) set([...(value || []), opt]);
                      }}
                      className={`px-5 py-3 rounded-full border text-sm transition-all ${
                        selected
                          ? "bg-[#C5A880] text-[#0C0E12] border-[#C5A880]"
                          : limitReached
                            ? "bg-[#161920] border-white/5 text-[#A29F9B]/50 cursor-not-allowed"
                            : "bg-[#161920] border-white/10 text-[#DCD7D2] hover:border-[#C5A880]/40"
                      }`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-[#A29F9B] mt-4">
                {(value || []).length} seleccionado{(value || []).length === 1 ? "" : "s"}
                {q.min ? ` · mínimo ${q.min}` : ""}{q.max ? ` · máximo ${q.max}` : ""}
              </p>
            </div>
          )}

          {q.type === "number" && (
            <div data-testid="onboarding-number">
              <input type="range" min={q.min} max={q.max} value={value ?? q.default}
                onChange={(e)=>set(Number(e.target.value))}
                className="w-full accent-[#C5A880]"
                data-testid={`range-${q.key}`} />
              <div className="mt-6 font-display text-5xl text-[#C5A880]">{value ?? q.default}</div>
            </div>
          )}

          {q.type === "range_pair" && (
            <div data-testid="onboarding-range-pair">
              <div className="grid grid-cols-2 gap-6">
                {[
                  { k: q.minKey, label: "Mínimo", def: q.defMin },
                  { k: q.maxKey, label: "Máximo", def: q.defMax },
                ].map(({k, label, def}) => (
                  <div key={k}>
                    <p className="text-xs text-[#A29F9B] mb-2">{label}</p>
                    <input type="range" min={q.min} max={q.max}
                      value={answers[k] ?? def}
                      onChange={(e)=>setKey(k, Number(e.target.value))}
                      className="w-full accent-[#C5A880]"
                      data-testid={`range-pair-${k}`} />
                    <div className="font-display text-3xl text-[#C5A880] mt-2">{answers[k] ?? def} cm</div>
                  </div>
                ))}
              </div>
              {answers[q.minKey] > answers[q.maxKey] && (
                <p className="text-xs text-[#7A2021] mt-3">O mínimo deve ser ≤ ao máximo.</p>
              )}
            </div>
          )}

          {q.type === "text" && (
            <textarea
              value={value || ""}
              onChange={(e)=>set(e.target.value.slice(0, q.maxLength || 200))}
              maxLength={q.maxLength || 200}
              rows={3}
              placeholder={q.placeholder}
              className="w-full bg-[#161920] border border-white/5 rounded-2xl p-4 text-[#F5F2EE] outline-none focus:border-[#C5A880]/50"
              data-testid={`text-${q.key}`}
            />
          )}

          {q.type === "keywords" && (
            <KeywordInput value={value || []} onChange={set} qkey={q.key} />
          )}

          </div>
        </div>
      </main>

      <footer className="px-6 md:px-12 py-6 border-t border-white/5 flex items-center justify-between">
        <button onClick={back} disabled={step === 0}
          data-testid="onboarding-back"
          className="btn-secondary inline-flex items-center gap-2 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <button onClick={next} disabled={!canNext() || submitting}
          data-testid="onboarding-next"
          className="btn-primary inline-flex items-center gap-2">
          {step === total - 1 ? (submitting ? "A guardar..." : "Concluir") : "Continuar"}
          <ChevronRight className="w-4 h-4" />
        </button>
      </footer>
    </div>
  );
};

const KeywordInput = ({ value, onChange, qkey }) => {
  const [t, setT] = useState("");
  const add = () => {
    const w = t.trim();
    if (!w || value.includes(w)) return;
    onChange([...value, w]);
    setT("");
  };
  const remove = (w) => onChange(value.filter(x => x !== w));
  return (
    <div data-testid={`keywords-${qkey}`}>
      <div className="flex gap-2 flex-wrap mb-4">
        {value.map((w) => (
          <span key={w} className="px-4 py-2 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/40 text-[#C5A880] text-sm flex items-center gap-2">
            {w}
            <button onClick={()=>remove(w)} className="text-[#A29F9B] hover:text-[#F5F2EE]" data-testid={`kw-remove-${w}`}>×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-3">
        <input className="input-elegant flex-1" placeholder="Escreve uma palavra e pressiona Enter"
          value={t} onChange={(e)=>setT(e.target.value)}
          onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); add(); }}}
          data-testid="kw-input" />
        <button type="button" onClick={add} className="btn-secondary" data-testid="kw-add">Adicionar</button>
      </div>
    </div>
  );
};

export default Onboarding;
